import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FlaskConical, Play, Save, Trash2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';
import { useTenantScope } from '../context/TenantScopeContext';

interface Parsed {
  host: string;
  format: string;
  message: string;
  fields?: Record<string, string>;
}

interface Sample {
  source: string;
  host: string;
  raw: string;
  occurrences: string;
  last_seen: string;
  parsed: Parsed;
}

interface Fixture {
  id: number;
  raw: string;
  note: string | null;
  expected: Parsed;
  last_status: 'pass' | 'fail' | null;
  last_run_at: string | null;
  created_by_name: string | null;
}

const FORMAT_STYLE: Record<string, string> = {
  cef: 'bg-emerald-50 text-emerald-700',
  keyvalue: 'bg-emerald-50 text-emerald-700',
  rfc5424: 'bg-sky-50 text-sky-700',
  rfc3164: 'bg-sky-50 text-sky-700',
  windows: 'bg-indigo-50 text-indigo-700',
  unknown: 'bg-red-50 text-red-700',
};

const SAMPLE_COLUMNS: ColumnDef[] = [
  { key: 'format', label: 'Format', defaultVisible: true },
  { key: 'host', label: 'Host', defaultVisible: true },
  { key: 'events', label: 'Events', defaultVisible: true },
  { key: 'raw', label: 'Raw', defaultVisible: true },
];

const FIXTURE_COLUMNS: ColumnDef[] = [
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'note', label: 'Note', defaultVisible: true },
  { key: 'expected', label: 'Expected', defaultVisible: true },
  { key: 'raw', label: 'Raw', defaultVisible: true },
];

export default function ParserLabPage() {
  const { orderedDefs: sampleDefs, moveColumn: moveSampleColumn } = useColumnPrefs('soc.columns.parser_samples', SAMPLE_COLUMNS);
  const { orderedDefs: fixtureDefs, moveColumn: moveFixtureColumn } = useColumnPrefs('soc.columns.parser_fixtures', FIXTURE_COLUMNS);
  const [line, setLine] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<Parsed | null>(null);
  const [samples, setSamples] = useState<Sample[]>([]);
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [running, setRunning] = useState(false);
  const [runSummary, setRunSummary] = useState<string | null>(null);
  // Tenant dei campioni reali: parte da quello scelto in "Viewing" ma si cambia qui senza
  // toccare la selezione globale. Le fixture restano globali.
  const { tenants, selectedTenantId } = useTenantScope();
  const [sampleTenantId, setSampleTenantId] = useState<number | null>(selectedTenantId);

  const load = useCallback(async () => {
    const [samplesRes, fixturesRes] = await Promise.all([
      axios.get('/api/parser/samples', { params: { tenantId: sampleTenantId ?? undefined } }),
      axios.get('/api/parser/fixtures'),
    ]);
    setSamples(samplesRes.data.data);
    setFixtures(fixturesRes.data.data);
  }, [sampleTenantId]);

  useEffect(() => { load().catch(() => undefined); }, [load]);

  const doPreview = async (value?: string) => {
    const raw = (value ?? line).trim();
    if (!raw) return;
    if (value) setLine(value);
    const res = await axios.post('/api/parser/preview', { line: raw });
    setPreview(res.data.data);
  };

  const saveFixture = async () => {
    if (!line.trim()) return;
    await axios.post('/api/parser/fixtures', { raw: line.trim(), note: note.trim() || undefined });
    setNote('');
    await load();
  };

  const runAll = async () => {
    setRunning(true);
    setRunSummary(null);
    try {
      const res = await axios.post('/api/parser/fixtures/run');
      const { total, failed } = res.data.data;
      setRunSummary(failed === 0 ? `All ${total} fixtures pass.` : `${failed} of ${total} fixtures FAILED: the parser changed behaviour.`);
      await load();
    } finally {
      setRunning(false);
    }
  };

  const removeFixture = async (id: number) => {
    await axios.delete(`/api/parser/fixtures/${id}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><FlaskConical size={20} /> Parser Lab</h1>
        <button className="btn-secondary flex items-center gap-2" onClick={() => load()}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Test dal vivo */}
      <div className="card p-4 space-y-3">
        <h2 className="font-medium">Test a raw log line</h2>
        <textarea
          className="input w-full font-mono text-xs h-24"
          placeholder="Paste a raw log line here (e.g. <134>Sep 4 14:43:34 CEF:0|Sangfor|NGAF|...)"
          value={line}
          onChange={e => setLine(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <button className="btn-primary flex items-center gap-2" onClick={() => doPreview()}>
            <Play size={16} /> Parse
          </button>
          <input
            className="input flex-1"
            placeholder="Note (device model, customer...)"
            value={note}
            onChange={e => setNote(e.target.value)}
          />
          <button className="btn-secondary flex items-center gap-2" onClick={saveFixture} disabled={!preview}>
            <Save size={16} /> Save as fixture
          </button>
        </div>
        {preview && (
          <div className="rounded border border-gray-200 p-3 text-sm space-y-1">
            <div><span className="text-gray-500">Format:</span>{' '}
              <span className={`badge ${FORMAT_STYLE[preview.format] || ''}`}>{preview.format}</span></div>
            <div><span className="text-gray-500">Host:</span> <span className="font-mono">{preview.host}</span>
              {preview.host === 'unknown' && <span className="text-amber-600 text-xs ml-2">(il gateway userà l'IP del mittente)</span>}</div>
            <div><span className="text-gray-500">Message:</span> <span className="font-mono text-xs">{preview.message}</span></div>
            {preview.fields && Object.keys(preview.fields).length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-500">Extracted fields ({Object.keys(preview.fields).length})</summary>
                <pre className="mt-1 bg-gray-50 rounded p-2 overflow-auto max-h-48">{JSON.stringify(preview.fields, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Campioni reali */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-medium">Recent real samples (one per device, last 7 days)</h2>
          <select className="input-field w-auto" value={sampleTenantId ?? ''}
            onChange={e => setSampleTenantId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">All tenants</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 uppercase">
            {sampleDefs.map(d => (
              <DraggableTh key={d.key} colKey={d.key} onMove={moveSampleColumn} className="py-1 pr-3 font-medium">
                {d.label}
              </DraggableTh>
            ))}
            <th></th>
          </tr></thead>
          <tbody>
            {samples.map((s, i) => (
              <tr key={i} className="border-t border-gray-100">
                {sampleDefs.map(d => (
                  <td key={d.key} className={d.key === 'raw' ? 'py-1.5 font-mono text-xs text-gray-500 truncate max-w-md' : d.key === 'host' ? 'py-1.5 pr-3 font-mono text-xs' : 'py-1.5 pr-3'}
                    title={d.key === 'raw' ? s.raw : undefined}>
                    {d.key === 'format' && <span className={`badge ${FORMAT_STYLE[s.parsed.format] || ''}`}>{s.parsed.format}</span>}
                    {d.key === 'host' && s.host}
                    {d.key === 'events' && s.occurrences}
                    {d.key === 'raw' && s.raw.slice(0, 120)}
                  </td>
                ))}
                <td className="py-1.5 text-right">
                  <button className="btn-secondary text-xs" onClick={() => doPreview(s.raw)}>Load</button>
                </td>
              </tr>
            ))}
            {samples.length === 0 && <tr><td colSpan={5} className="py-3 text-gray-400">No events in the last 7 days.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Fixture di regressione */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium">Regression fixtures</h2>
          <button className="btn-primary flex items-center gap-2" onClick={runAll} disabled={running || fixtures.length === 0}>
            <Play size={16} /> {running ? 'Running…' : 'Run all'}
          </button>
        </div>
        {runSummary && <div className="text-sm">{runSummary}</div>}
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 uppercase">
            {fixtureDefs.map(d => (
              <DraggableTh key={d.key} colKey={d.key} onMove={moveFixtureColumn} className="py-1 pr-3 font-medium">
                {d.label}
              </DraggableTh>
            ))}
            <th></th>
          </tr></thead>
          <tbody>
            {fixtures.map(f => (
              <tr key={f.id} className="border-t border-gray-100">
                {fixtureDefs.map(d => (
                  <td key={d.key} className={d.key === 'raw' ? 'py-1.5 font-mono text-xs text-gray-500 truncate max-w-md' : d.key === 'expected' ? 'py-1.5 pr-3 text-xs' : 'py-1.5 pr-3'}
                    title={d.key === 'raw' ? f.raw : undefined}>
                    {d.key === 'status' && (
                      f.last_status === 'pass' ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={14} /> pass</span>
                      : f.last_status === 'fail' ? <span className="text-red-600 flex items-center gap-1"><XCircle size={14} /> FAIL</span>
                      : <span className="text-gray-400">—</span>
                    )}
                    {d.key === 'note' && (f.note || <span className="text-gray-400">—</span>)}
                    {d.key === 'expected' && (
                      <>
                        <span className={`badge ${FORMAT_STYLE[f.expected.format] || ''}`}>{f.expected.format}</span>{' '}
                        <span className="font-mono">{f.expected.host}</span>
                      </>
                    )}
                    {d.key === 'raw' && f.raw.slice(0, 100)}
                  </td>
                ))}
                <td className="py-1.5 text-right">
                  <button className="text-gray-400 hover:text-red-600" onClick={() => removeFixture(f.id)} title="Delete fixture">
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {fixtures.length === 0 && <tr><td colSpan={5} className="py-3 text-gray-400">No fixtures saved yet: parse a line above and save it.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
